import { useEffect, useRef } from "react";
import { Requirement } from "../../types/requirement.response";
import { AddableCourse, CourseLite } from "../../types/course.response";
import { ProgramSummaryResponse } from "../../types/program.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import ReqSectionHeader from "./ReqSectionHeader";
import { CourseRowSkeletonRows } from "../loading/CourseRowSkeleton";

interface RequirementSectionPlaceholderProps {
    program: ProgramSummaryResponse;
    requirement: Requirement;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void | Promise<void>;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
    fromHome?: boolean;
    onRequirementClick?: (programId: string, requirementId: string) => void;
    /**
     * 注册到页面共享的 IntersectionObserver(视口预取,渐进加载契约 §3.1);
     * 返回注销函数。programId/requirementId 由根元素 data 属性携带。
     */
    onRegister?: (el: HTMLElement) => () => void;
}

/**
 * requirement 切片未到达时的占位区块:区块头 + 进度是 skeleton 自带的真实数据,
 * 行区域为固定高度的骨架行防 CLS(契约 §5.2 第 3 步)。切片到达后由
 * RequirementSection 原样替换,外层布局不变。
 */
export default function RequirementSectionPlaceholder({
    program,
    requirement,
    handleAddCourse,
    handleDeleteCourse,
    handleOverride,
    fromHome,
    onRequirementClick,
    onRegister,
}: RequirementSectionPlaceholderProps) {
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!rootRef.current || !onRegister) return;
        return onRegister(rootRef.current);
    }, [onRegister]);

    return (
        <div
            ref={rootRef}
            data-program-id={program.info.id}
            data-requirement-id={requirement.info.id}
            className="transition-all pb-12 md:pb-16"
        >
            <ReqSectionHeader
                program={program}
                requirement={requirement}
                handleAddCourse={handleAddCourse}
                handleDeleteCourse={handleDeleteCourse}
                handleOverride={handleOverride}
                fromHome={fromHome}
                onRequirementClick={onRequirementClick}
            />
            <table className="w-full border-collapse mt-4">
                <tbody>
                    <CourseRowSkeletonRows rows={3} />
                </tbody>
            </table>
        </div>
    );
}
